/**
 * Brand Directory (US-023.2)
 *
 * Grid of brand cards with search, industry/sport/deal-size filters, and sort.
 * Clicking a card navigates to the brand detail page. Match scoring badges
 * will be added by PR-D on top of this.
 */

import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@app/ui';
import { PageTransition, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { formatCurrency } from '@app/validators';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { RouteErrorComponent } from '../../../components/route-error';
import { api } from '../../../lib/api';
import { isFeatureGateError } from '../../../lib/feature-gate-client';

// Radix Select disallows empty-string values — use a sentinel for "all"
// buckets and translate at the boundary.
const ALL = '__all__';

// Filter option lists (kept in sync with the @app/validators enums used by
// PR-B's athlete profile).
const INDUSTRIES = [
  { value: ALL, label: 'All industries' },
  { value: 'apparel', label: 'Apparel' },
  { value: 'footwear', label: 'Footwear' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'tech', label: 'Tech' },
  { value: 'finance', label: 'Finance' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'beauty', label: 'Beauty & Personal Care' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'health_wellness', label: 'Health & Wellness' },
  { value: 'nutrition', label: 'Nutrition' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'retail', label: 'Retail' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'education', label: 'Education' },
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'other', label: 'Other' },
] as const;

const SPORTS = [
  { value: ALL, label: 'All sports' },
  { value: 'football', label: 'Football' },
  { value: 'mens_basketball', label: "Men's Basketball" },
  { value: 'womens_basketball', label: "Women's Basketball" },
  { value: 'baseball', label: 'Baseball' },
  { value: 'softball', label: 'Softball' },
  { value: 'volleyball', label: 'Volleyball' },
  { value: 'soccer', label: 'Soccer' },
  { value: 'track_field', label: 'Track & Field' },
  { value: 'gymnastics', label: 'Gymnastics' },
  { value: 'lacrosse', label: 'Lacrosse' },
  { value: 'wrestling', label: 'Wrestling' },
  { value: 'hockey', label: 'Hockey' },
  { value: 'swimming', label: 'Swimming' },
  { value: 'tennis', label: 'Tennis' },
  { value: 'golf', label: 'Golf' },
  { value: 'cross_country', label: 'Cross Country' },
  { value: 'action_sports', label: 'Action Sports' },
  { value: 'esports', label: 'Esports' },
] as const;

const DEAL_SIZE_BUCKETS = [
  { value: ALL, label: 'Any size' },
  { value: '500', label: '$500+' },
  { value: '1000', label: '$1,000+' },
  { value: '5000', label: '$5,000+' },
  { value: '25000', label: '$25,000+' },
  { value: '100000', label: '$100,000+' },
] as const;

// NOTE: 'match' sorts client-side using scores from the match router — not
// handed to the backend query. The server still orders by totalDeals; we
// resort in place after scores arrive.
const SORT_OPTIONS = [
  { value: 'match', label: 'Best match' },
  { value: 'totalDeals', label: 'Most active' },
  { value: 'dealRangeMax', label: 'Largest deals' },
  { value: 'name', label: 'Name (A–Z)' },
] as const;

type SortBy = (typeof SORT_OPTIONS)[number]['value'];

export const Route = createFileRoute('/_authenticated/brands/')({
  validateSearch: z.object({
    search: z.string().optional(),
    industry: z.string().optional(),
    sport: z.string().optional(),
    dealSizeMin: z.coerce.number().optional(),
    // 'match' is a client-side sort that reorders by match score after the
    // match router returns. The server doesn't receive 'match' — see loader.
    sortBy: z.enum(['match', 'totalDeals', 'name', 'dealRangeMax']).default('match'),
    page: z.coerce.number().default(1),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const { getServerApi } = await import('../../../lib/api-server');
    const serverApi = await getServerApi();
    // Server doesn't understand 'match' sort (it's client-side); collapse
    // to totalDeals for the DB query, then the component resorts by score.
    const serverSortBy = deps.sortBy === 'match' ? 'totalDeals' : deps.sortBy;
    await context.queryClient.ensureQueryData(
      serverApi.brand.list.queryOptions({
        input: {
          search: deps.search,
          industry: deps.industry,
          sport: deps.sport,
          dealSizeMin: deps.dealSizeMin,
          sortBy: serverSortBy,
          page: deps.page,
          limit: 20,
        },
      }),
    );
  },
  pendingComponent: () => (
    <div className="animate-pulse">
      <div className="h-8 bg-muted rounded w-48 mb-6" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-40 bg-muted rounded" />
        ))}
      </div>
    </div>
  ),
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: Brands,
});

function Brands() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // Debounced search input.
  const [searchInput, setSearchInput] = useState(search.search || '');
  const currentSearch = search.search || '';
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== currentSearch) {
        navigate({
          search: (prev) => ({ ...prev, search: searchInput || undefined, page: 1 }),
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, currentSearch, navigate]);

  const serverSortBy = search.sortBy === 'match' ? 'totalDeals' : search.sortBy;
  const { data } = useSuspenseQuery(
    api.brand.list.queryOptions({
      input: {
        search: search.search,
        industry: search.industry,
        sport: search.sport,
        dealSizeMin: search.dealSizeMin,
        sortBy: serverSortBy,
        page: search.page,
        limit: 20,
      },
    }),
  );

  const { total, page, limit } = data;
  const totalPages = Math.ceil(total / limit);

  // Fetch match scores for the visible page of brands (US-023.6).
  // Uses useQuery (not suspense) so the grid renders immediately and
  // badges fade in once scores return.
  const brandIds = data.brands.map((b) => b.id);
  const { data: matchData, error: matchError } = useQuery({
    ...api.match.forBrands.queryOptions({ input: { brandIds } }),
    enabled: brandIds.length > 0,
  });
  // `match.forBrands` is gated by the `brandMatching` plan feature.
  // For free users we still show the directory (just without badges or the
  // 'Best match' sort) and surface an upsell banner instead of silently
  // hiding the personalization. Non-gate errors fall through to no-data
  // state — same as before.
  const matchGated = matchError !== null && isFeatureGateError(matchError);

  // Reorder brands by match score when sortBy === 'match' and scores are in.
  const brands =
    search.sortBy === 'match' && matchData?.scorable
      ? [...data.brands].sort(
          (a, b) => (matchData.scores[b.id] ?? 0) - (matchData.scores[a.id] ?? 0),
        )
      : data.brands;

  // Helpers to update a single filter without stomping the others.
  const setFilter = <K extends keyof typeof search>(key: K, value: (typeof search)[K] | undefined) =>
    navigate({
      search: (prev) => ({
        ...prev,
        [key]: value === '' || value === undefined ? undefined : value,
        page: 1,
      }),
    });

  const anyFilterActive = !!(
    search.search ||
    search.industry ||
    search.sport ||
    search.dealSizeMin
  );

  return (
    <PageTransition>
      <h1 className="text-2xl sm:text-3xl font-bold mb-2">Brand Directory</h1>
      <p className="text-muted-foreground mb-6">
        Browse brands that work with college athletes ({total} brand{total === 1 ? '' : 's'})
      </p>

      {/* Search + filters */}
      <div className="mb-6 space-y-3">
        <Input
          placeholder="Search by name, mission, industry..."
          className="w-full sm:max-w-md"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <FilterSelect
            value={search.industry ?? ALL}
            onChange={(v) => setFilter('industry', v === ALL ? undefined : v)}
            options={INDUSTRIES}
            placeholder="Industry"
          />
          <FilterSelect
            value={search.sport ?? ALL}
            onChange={(v) => setFilter('sport', v === ALL ? undefined : v)}
            options={SPORTS}
            placeholder="Sport"
          />
          <FilterSelect
            value={search.dealSizeMin ? String(search.dealSizeMin) : ALL}
            onChange={(v) =>
              setFilter('dealSizeMin', v === ALL ? undefined : Number(v))
            }
            options={DEAL_SIZE_BUCKETS}
            placeholder="Min deal size"
          />
          <FilterSelect
            value={search.sortBy}
            onChange={(v) => setFilter('sortBy', (v as SortBy) || 'totalDeals')}
            options={SORT_OPTIONS}
            placeholder="Sort by"
          />
        </div>
        {anyFilterActive && (
          <button
            type="button"
            onClick={() =>
              navigate({
                search: () => ({ sortBy: 'totalDeals', page: 1 }),
              })
            }
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Plan-gated upsell — shown when the user's plan doesn't include
          brand-match scoring. Free users see the directory but no match
          badges; this banner explains why and links to /pricing. */}
      {matchGated && (
        <div className="mb-4 rounded-md border border-primary/40 bg-primary/5 px-4 py-3 flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Personalized match scores are part of the Pro plan. Upgrade to see how each brand
            aligns with your sport, values, and audience.
          </p>
          <Link to="/pricing" search={{ limit: 'brandMatching' }} className="shrink-0">
            <Button size="sm">See Pro plans</Button>
          </Link>
        </div>
      )}

      {/* Profile completion nudge — shown when scores can't be computed yet */}
      {!matchGated && matchData !== undefined && !matchData.scorable && (
        <div className="mb-4 rounded-md border bg-muted/40 px-4 py-3 flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Add your sport, values, or deal preferences to your profile to see personalized match
            scores.
          </p>
          <Link to="/profile" className="shrink-0">
            <Button size="sm" variant="outline">
              Complete profile
            </Button>
          </Link>
        </div>
      )}

      {/* Results */}
      {brands.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {anyFilterActive
                ? 'No brands match your filters. Try broadening them.'
                : 'Brand directory is empty. Track your deals to help build the database.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {brands.map((brand) => (
              <StaggerItem key={brand.id}>
                <Link
                  to="/brands/$brandId"
                  params={{ brandId: brand.id }}
                  className="block h-full"
                >
                  <Card className="hover:shadow-md transition-shadow h-full">
                    <CardContent className="pt-6 space-y-3">
                      <div className="flex items-start gap-3">
                        {brand.logo ? (
                          <img
                            src={brand.logo}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium shrink-0">
                            {brand.name.charAt(0)}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold">{brand.name}</p>
                          {brand.industry && (
                            <p className="text-xs text-muted-foreground capitalize">
                              {brand.industry.replace(/_/g, ' ')}
                            </p>
                          )}
                        </div>
                        {matchData?.scorable && matchData.scores[brand.id] !== undefined && (
                          <MatchBadge score={matchData.scores[brand.id] ?? 0} />
                        )}
                      </div>

                      {brand.mission && (
                        <p className="text-sm text-muted-foreground line-clamp-2 italic">
                          "{brand.mission}"
                        </p>
                      )}

                      {brand.sportsFocus.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {brand.sportsFocus.slice(0, 3).map((s: string) => (
                            <span
                              key={s}
                              className="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
                            >
                              {s.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {brand.sportsFocus.length > 3 && (
                            <span className="text-xs text-muted-foreground py-0.5">
                              +{brand.sportsFocus.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="flex gap-3 text-xs text-muted-foreground pt-1 border-t">
                        <span>
                          {brand.totalDeals} deal{brand.totalDeals !== 1 ? 's' : ''}
                        </span>
                        {brand.dealRangeMax != null && brand.dealRangeMin != null && (
                          <span>
                            {formatCurrency(brand.dealRangeMin)} –{' '}
                            {formatCurrency(brand.dealRangeMax)}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => navigate({ search: { ...search, page: page - 1 } })}
              >
                Previous
              </Button>
              <span className="flex items-center text-sm text-muted-foreground px-3">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => navigate({ search: { ...search, page: page + 1 } })}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </PageTransition>
  );
}

/**
 * Match score badge — 0–100% chip on each brand card.
 * Color bands (loose, not a rigorous mapping):
 *   80+ : strong match → primary
 *   60+ : good match → accent
 *   40+ : okay match → muted border
 *   <40 : weak match → muted, dimmed
 */
function MatchBadge({ score }: { score: number }) {
  const rounded = Math.round(score);
  const style =
    rounded >= 80
      ? 'bg-primary text-primary-foreground'
      : rounded >= 60
        ? 'bg-primary/20 text-primary border border-primary/40'
        : rounded >= 40
          ? 'bg-muted text-muted-foreground border'
          : 'bg-muted/50 text-muted-foreground border opacity-70';
  return (
    <span
      title={`Match score: ${rounded}%`}
      className={`inline-flex items-center justify-center rounded-full text-xs font-semibold w-11 h-11 shrink-0 ${style}`}
      aria-label={`Match score ${rounded}%`}
    >
      {rounded}%
    </span>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly { value: string; label: string }[];
  placeholder: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
